import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Shield } from "lucide-react";
import { API, COVER_IMAGE, STUDIO_LOGO, SSB_PLACEHOLDER } from "../lib/constants";
import AgeGate from "../components/AgeGate";

export default function SeriesDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [stats, setStats] = useState({ episodes: 0 });

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const res = await axios.get(`${API}/series/${slug}`);
        setSeries(res.data);
        if (res.data.content_rating === '17+') {
          const verified = localStorage.getItem('age_verified_ssb');
          if (!verified || location.state?.needsAgeGate) {
            setShowAgeGate(true);
          }
        }
        const statsRes = await axios.get(`${API}/stats?series=${slug}`);
        setStats(statsRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchSeries();
  }, [slug, location.state]);

  const handleAgeVerify = (confirmed) => {
    if (confirmed) {
      localStorage.setItem('age_verified_ssb', 'true');
      setShowAgeGate(false);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-gray-500">Series not found</p>
      </div>
    );
  }

  const coverImg = series.cover_image || SSB_PLACEHOLDER;
  const seasonNames = series.season_names || {};

  return (
    <div data-testid="series-detail-page">
      {showAgeGate && <AgeGate onVerify={handleAgeVerify} />}

      <section className="hero-section grain" style={{ minHeight: '70vh' }}>
        <div className="hero-bg" style={{ backgroundImage: `url(${coverImg})` }} />
        <div className="hero-overlay" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <img src={STUDIO_LOGO} alt="Red Ink Studios" className="w-24 h-24 mx-auto mb-4 object-contain fade-in" />
          <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-2 fade-in">RED INK STUDIOS PRESENTS</p>
          {series.content_rating === '17+' && (
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-600/80 text-white text-xs font-bold tracking-wider rounded mb-4 fade-in">
              <Shield size={12} /> MATURE CONTENT — 17+
            </div>
          )}
          <h1 className="font-cinzel text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 neon-red fade-in" style={{ animationDelay: '0.2s' }}>
            {(series.title || series.name || '').toUpperCase()}
          </h1>
          <p className="font-manrope text-gray-400 text-lg max-w-2xl mx-auto mb-4 fade-in" style={{ animationDelay: '0.3s' }}>
            {series.tagline}
          </p>
          <p className="font-manrope text-gray-500 text-sm max-w-xl mx-auto mb-10 fade-in" style={{ animationDelay: '0.4s' }}>
            {series.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in" style={{ animationDelay: '0.6s' }}>
            <button onClick={() => navigate(`/series/${slug}/episodes`)} className="btn-primary" data-testid="series-start-reading-btn">
              Start Reading
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary" data-testid="series-back-btn">
              All Series
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-gradient-to-b from-[#050505] to-[#0a0a0a]">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8 text-center">
          <div>
            <p className="font-anton text-4xl text-white">{series.seasons}</p>
            <p className="font-mono text-xs tracking-widest text-gray-500 mt-2">SEASONS</p>
          </div>
          <div>
            <p className="font-anton text-4xl text-white">{stats.episodes}</p>
            <p className="font-mono text-xs tracking-widest text-gray-500 mt-2">EPISODES</p>
          </div>
          <div>
            <p className="font-anton text-4xl text-white">{series.content_rating}</p>
            <p className="font-mono text-xs tracking-widest text-gray-500 mt-2">RATING</p>
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-cinzel text-3xl text-center mb-12 text-red-500">SEASONS</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {Array.from({ length: series.seasons }, (_, i) => i + 1).map(s => (
              <button
                key={s}
                onClick={() => navigate(`/series/${slug}/episodes?season=${s}`)}
                className="group p-6 border border-white/10 rounded-lg hover:border-red-500/40 transition-all bg-white/[0.02] text-left"
                data-testid={`series-season-${s}`}
              >
                <span className="font-anton text-5xl text-red-500/30 group-hover:text-red-500/60 transition-colors">
                  {String(s).padStart(2, '0')}
                </span>
                <h3 className="font-cinzel text-lg font-bold mt-2 group-hover:text-red-500 transition-colors">
                  Season {s}
                </h3>
                {seasonNames[String(s)] && (
                  <p className="text-sm text-gray-500 mt-1">{seasonNames[String(s)]}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
